import { useEffect, useState } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";
import useInstallPrompt from "@/lib/hooks/useInstallPrompt";

const DISMISS_KEY = "little-oak:pwa-install-dismissed-at";
const SNOOZE_MS   = 1000 * 60 * 60 * 24 * 14; // 14 days

/**
 * Parent home · dismissible PWA install card. Shows up when:
 *   * The browser fired `beforeinstallprompt` (Chromium/Edge/Brave).
 *   * OR the user is on iOS Safari and hasn't added to home screen yet
 *     (iOS Safari doesn't fire beforeinstallprompt — we show a hint
 *     instead of a CTA).
 *
 * Dismissals are persisted for 14 days via localStorage so we don't
 * nag parents who deliberately skipped.
 */
export default function PWAInstallCard() {
  const { isInstallable, promptInstall, isIOS, isStandalone } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const ts = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
      if (ts && Date.now() - ts < SNOOZE_MS) setDismissed(true);
    } catch {
      /* ignore — private browsing */
    }
  }, []);

  // Hidden states:
  //   * already installed/running as standalone
  //   * user dismissed inside the snooze window
  //   * neither Chromium prompt nor iOS Safari (nothing useful to say)
  if (isStandalone || dismissed) return null;

  const showIOSHint = isIOS && !isInstallable;
  if (!isInstallable && !showIOSHint) return null;

  const onDismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const onInstall = async () => {
    const result = await promptInstall();
    if (result?.outcome === "accepted") {
      setDismissed(true); // hide immediately even before `appinstalled`
    }
  };

  return (
    <section
      data-testid="pwa-install-card"
      className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-white to-[#FAF6EF]/60 p-5 shadow-sm"
    >
      <button
        type="button"
        aria-label="Dismiss install prompt"
        data-testid="pwa-install-dismiss"
        onClick={onDismiss}
        className="absolute right-3 top-3 rounded-full p-1.5 text-neutral-500 transition hover:bg-white/60 hover:text-neutral-800"
      >
        <X size={14} weight="bold" />
      </button>

      <div className="flex items-start gap-4 pr-6">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-primary text-white shadow-sm">
          <DownloadSimple size={20} weight="duotone" />
        </div>
        <div className="min-w-0">
          <p className="font-heading text-base font-semibold text-neutral-900">
            Install HoneyAcorn on your home screen
          </p>

          {isInstallable ? (
            <>
              <p className="mt-1 text-xs leading-relaxed text-neutral-600">
                Faster launches, full-screen layout, and instant notifications for
                new messages — all in a tap.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  data-testid="pwa-install-btn"
                  onClick={onInstall}
                  className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-primary-hover"
                >
                  Install
                </button>
                <button
                  type="button"
                  data-testid="pwa-install-maybe-later"
                  onClick={onDismiss}
                  className="rounded-full px-3 py-2 text-xs font-semibold text-neutral-500 transition hover:text-neutral-800"
                >
                  Maybe later
                </button>
              </div>
            </>
          ) : (
            <p
              data-testid="pwa-install-ios-hint"
              className="mt-1 text-xs leading-relaxed text-neutral-600"
            >
              Tap the Share button in Safari, then choose <strong>Add to Home Screen</strong>.
              You'll get push notifications and a full-screen app icon.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
